// ASIN Research Groups — user-curated collections of Amazon ASINs that get
// Apify-scraped on demand. Layout mirrors the Bestseller URLs sub-tab:
//
//   ┌──────────────────────────┬──────────────────────────────────┐
//   │ Input panel              │ Live scrape progress / results   │
//   │ (Group name + ASIN list  │ for the most recent group        │
//   │  + marketplace + CTA)    │                                  │
//   └──────────────────────────┴──────────────────────────────────┘
//   │ Past Research Groups · re-scrape / delete                   │
//   └──────────────────────────────────────────────────────────────┘
//
// The created groups also appear in Market Research Analytics as
// violet-themed cards alongside categories (handled in App.js).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Layers, RefreshCw, Trash2, Loader2, Play, CheckCircle2, AlertCircle, Clock,
  Sparkles, ListChecks, Package,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND = "#7c3aed"; // violet-600 — distinct from market-research purple/amber

const fmtRelTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return `${Math.round(diff / 86400)} d ago`;
};

const STATUS_STYLES = {
  running:   { color: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-200",   Icon: Loader2,      spin: true, label: "Scraping" },
  completed: { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", Icon: CheckCircle2,             label: "Ready" },
  failed:    { color: "text-rose-700",    bg: "bg-rose-50",    border: "border-rose-200",    Icon: AlertCircle,              label: "Failed" },
};

const ResearchGroupsModule = () => {
  // ─── Input form state ───
  const [name, setName] = useState("");
  const [asinsRaw, setAsinsRaw] = useState("");
  const [marketplace, setMarketplace] = useState("IN");
  const [saving, setSaving] = useState(false);

  // ─── Groups & latest-job tracking ───
  const [groups, setGroups] = useState([]);
  const [latestGroupId, setLatestGroupId] = useState(null);

  const loadGroups = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/research-groups`);
      setGroups(r.data?.groups || []);
    } catch {
      toast.error("Failed to load research groups");
    }
  }, []);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  // Poll while ANY group is mid-scrape so the right-panel progress is live.
  useEffect(() => {
    if (!groups.some(g => g.last_scrape_status === "running")) return;
    const id = setInterval(loadGroups, 4000);
    return () => clearInterval(id);
  }, [groups, loadGroups]);

  // Parse + validate ASINs live as the user types
  const parsedAsins = useMemo(() => {
    if (!asinsRaw.trim()) return { valid: [], invalid: [] };
    const valid = new Set();
    const invalid = [];
    const tokens = asinsRaw.split(/[\s,;\n\r\t]+/).filter(Boolean);
    tokens.forEach(t => {
      const up = t.trim().toUpperCase();
      if (/^[A-Z0-9]{10}$/.test(up)) valid.add(up);
      else if (up) invalid.push(t);
    });
    return { valid: Array.from(valid), invalid };
  }, [asinsRaw]);

  const canSave = name.trim().length >= 2 && parsedAsins.valid.length > 0 && !saving;
  const latestGroup = useMemo(
    () => (latestGroupId ? groups.find(g => g.group_id === latestGroupId) : groups[0]) || null,
    [groups, latestGroupId]
  );

  const handleSubmit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/research-groups`, {
        name: name.trim(),
        asins: parsedAsins.valid,
        marketplace,
      });
      const g = r.data;
      setLatestGroupId(g.group_id);
      toast.success(`Created "${g.name}" — scraping ${g.asins.length} ASIN${g.asins.length !== 1 ? "s" : ""}…`);
      setName("");
      setAsinsRaw("");
      await loadGroups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create group");
    } finally {
      setSaving(false);
    }
  };

  const handleRescrape = useCallback(async (g) => {
    try {
      await axios.post(`${API}/research-groups/${g.group_id}/rescrape`);
      setLatestGroupId(g.group_id);
      toast.success(`Re-scraping "${g.name}"…`);
      await loadGroups();
    } catch {
      toast.error("Re-scrape failed");
    }
  }, [loadGroups]);

  const handleDelete = useCallback(async (g) => {
    if (!window.confirm(`Delete "${g.name}" and all its scrape data?`)) return;
    try {
      await axios.delete(`${API}/research-groups/${g.group_id}`);
      toast.success(`Deleted "${g.name}"`);
      if (latestGroupId === g.group_id) setLatestGroupId(null);
      await loadGroups();
    } catch {
      toast.error("Delete failed");
    }
  }, [loadGroups, latestGroupId]);

  return (
    <div className="space-y-5" data-testid="research-groups-module">
      {/* ─── Header band — same shape as the Bestseller URLs sub-tab ─── */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-xl px-5 py-4" data-testid="research-groups-header">
        <div className="w-10 h-10 rounded-xl bg-white border border-violet-300 flex items-center justify-center">
          <Layers className="w-5 h-5 text-violet-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-gray-900">Research Groups</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Bundle ASINs from any category and we'll Apify-scrape them as a single research set —
            results appear in Market Research Analytics alongside your categories.
          </p>
        </div>
      </div>

      {/* ─── 2-column body — matches Bestseller URLs exactly ─── */}
      <div className="grid grid-cols-12 gap-5 items-start">
        {/* ─── LEFT COLUMN: input panel ─── */}
        <div className="col-span-12 lg:col-span-4 space-y-4 sticky top-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <div className="text-base font-semibold text-gray-900 mb-4">Input</div>

            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-1 block">Group name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Whey Protein Cross-Category Watch"
                  className="w-full h-9 px-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300 placeholder:text-gray-300"
                  data-testid="research-groups-name-input"
                />
                <div className="text-[10px] text-gray-400 mt-1">Shown as a card in Market Research Analytics.</div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide">ASINs</label>
                  <span className="text-[10px] text-gray-400">
                    {parsedAsins.valid.length > 0 && (
                      <span className="font-semibold text-violet-700 tabular-nums">{parsedAsins.valid.length} valid</span>
                    )}
                    {parsedAsins.invalid.length > 0 && (
                      <span className="ml-1.5 text-rose-600 tabular-nums">· {parsedAsins.invalid.length} invalid</span>
                    )}
                  </span>
                </div>
                <textarea
                  value={asinsRaw}
                  onChange={(e) => setAsinsRaw(e.target.value)}
                  placeholder={'B07XJ8C8F5\nB00LLZ82O4\nB0DBL1T67V\n\nOne per line · comma/space also OK'}
                  rows={6}
                  className="w-full text-xs font-mono px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300 placeholder:text-gray-300 resize-y"
                  data-testid="research-groups-asins-input"
                />
                <div className="text-[10px] text-gray-400 mt-1">
                  10-char Amazon product IDs · paste any quantity, dupes are ignored
                </div>
              </div>

              <div>
                <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-1 block">Marketplace</label>
                <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50">
                  {["IN", "US", "UK"].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMarketplace(m)}
                      className={`px-3 h-7 text-[11px] font-semibold rounded transition-all ${marketplace === m ? "bg-white shadow-sm text-violet-700" : "text-gray-500 hover:text-gray-700"}`}
                      data-testid={`research-groups-mkt-${m}`}
                    >
                      <img
                        src={`https://cdn.jsdelivr.net/gh/HatScripts/circle-flags@gh-pages/flags/${({IN:'in',US:'us',UK:'gb'}[m])}.svg`}
                        alt={m} width={11} height={11} draggable={false}
                        className="inline-block rounded-full mr-1.5 align-text-bottom" style={{width:'11px', height:'11px'}}
                      />
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="text-[10px] text-gray-400 flex items-center gap-1">
                <Package className="w-3 h-3" />
                Engine: <code className="font-mono px-1 py-0.5 bg-gray-50 border border-gray-200 rounded">Apify · junglee/Amazon-crawler</code>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSave}
            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl text-sm font-bold text-white bg-gradient-to-br from-violet-600 to-indigo-700 hover:from-violet-700 hover:to-indigo-800 shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="research-groups-submit-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {saving ? "Creating group…" : (parsedAsins.valid.length > 0
              ? `Start Extraction · ${parsedAsins.valid.length} ASIN${parsedAsins.valid.length !== 1 ? "s" : ""}`
              : "Start Extraction")}
          </button>
        </div>

        {/* ─── RIGHT COLUMN: live progress / results of the most-recent group ─── */}
        <div className="col-span-12 lg:col-span-8">
          <LatestGroupPanel
            group={latestGroup}
            onRescrape={handleRescrape}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {/* ─── Past Research Groups strip ─── */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="research-groups-past">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="w-4 h-4 text-violet-600" />
          <h4 className="text-sm font-bold text-gray-900">Past Research Groups</h4>
          <span className="text-[11px] font-mono text-gray-400 tabular-nums">{groups.length}</span>
          <button
            onClick={loadGroups}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded text-[10.5px] font-semibold text-gray-500 hover:text-violet-700 hover:bg-violet-50 transition-colors"
            data-testid="research-groups-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {groups.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400">
            <Sparkles className="w-5 h-5 mx-auto mb-2 text-violet-300" />
            No groups yet — fill the form above to create your first.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
                  <th className="text-left pl-2 py-2 font-bold">Name</th>
                  <th className="text-center py-2 font-bold">ASINs</th>
                  <th className="text-center py-2 font-bold">Market</th>
                  <th className="text-center py-2 font-bold">Status</th>
                  <th className="text-left py-2 font-bold">Scraped</th>
                  <th className="text-right pr-2 py-2 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const st = STATUS_STYLES[g.last_scrape_status] || STATUS_STYLES.completed;
                  const StIcon = st.Icon;
                  return (
                    <tr key={g.group_id} className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50" data-testid={`past-group-row-${g.group_id}`}>
                      <td className="pl-2 py-2.5">
                        <div className="font-semibold text-gray-800 truncate max-w-[260px]" title={g.name}>{g.name}</div>
                      </td>
                      <td className="text-center py-2.5 tabular-nums text-gray-700">{g.asins.length}</td>
                      <td className="text-center py-2.5">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[10px] font-semibold border border-gray-200">
                          <img src={`https://cdn.jsdelivr.net/gh/HatScripts/circle-flags@gh-pages/flags/${({IN:'in',US:'us',UK:'gb'}[g.marketplace] || 'in')}.svg`} alt="" width={10} height={10} className="rounded-full" style={{width:'10px', height:'10px'}} />
                          {g.marketplace || 'IN'}
                        </span>
                      </td>
                      <td className="text-center py-2.5">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${st.color} ${st.bg} border ${st.border}`}>
                          <StIcon className={`w-2.5 h-2.5 ${st.spin ? 'animate-spin' : ''}`} />
                          {st.label}
                          {g.last_scrape_status === "completed" && (g.last_scrape_total || 0) > 0 && (
                            <span className="font-normal text-gray-500 ml-0.5">{g.last_scrape_ok || 0}/{g.last_scrape_total}</span>
                          )}
                        </span>
                      </td>
                      <td className="py-2.5 text-gray-500 text-[11px]">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtRelTime(g.last_scrape_at)}</span>
                      </td>
                      <td className="pr-2 py-2.5 text-right">
                        <button
                          onClick={() => handleRescrape(g)}
                          disabled={g.last_scrape_status === "running"}
                          className="inline-flex items-center gap-1 px-2 py-1 mr-1 rounded text-[10px] font-semibold text-gray-500 hover:text-violet-700 hover:bg-violet-50 transition-colors disabled:opacity-40"
                          title="Re-scrape"
                          data-testid={`past-group-rescrape-${g.group_id}`}
                        >
                          <RefreshCw className={`w-3 h-3 ${g.last_scrape_status === "running" ? "animate-spin" : ""}`} />
                          Re-scrape
                        </button>
                        <button
                          onClick={() => handleDelete(g)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold text-gray-400 hover:text-rose-700 hover:bg-rose-50 transition-colors"
                          title="Delete"
                          data-testid={`past-group-delete-${g.group_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Right-side panel showing the most-recent group's live status. Mirrors how
// the Bestseller URLs sub-tab uses its right panel for the running job.
const LatestGroupPanel = ({ group, onRescrape, onDelete }) => {
  if (!group) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl px-6 py-16 text-center" data-testid="research-groups-empty-right">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 mb-3">
          <Layers className="w-7 h-7 text-violet-700" />
        </div>
        <h4 className="text-base font-bold text-gray-900">Build your first research group</h4>
        <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
          Paste ASINs into the form on the left — HORIZON scrapes them via Apify and the result appears as a violet card in Market Research Analytics alongside your categories.
        </p>
      </div>
    );
  }
  const st = STATUS_STYLES[group.last_scrape_status] || STATUS_STYLES.completed;
  const StIcon = st.Icon;
  const total = group.last_scrape_total || group.asins.length;
  const ok = group.last_scrape_ok || 0;
  const pct = total > 0 ? Math.round((ok / total) * 100) : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="research-groups-latest-panel">
      <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-violet-100 to-indigo-100 inline-flex items-center justify-center">
          <Layers className="w-5 h-5 text-violet-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-gray-900 truncate" title={group.name}>{group.name}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            <span className="font-semibold tabular-nums text-gray-700">{group.asins.length}</span> ASIN{group.asins.length !== 1 ? "s" : ""} ·
            <span className="ml-1">{group.marketplace || "IN"}</span> ·
            <span className="inline-flex items-center gap-1 ml-1"><Clock className="w-2.5 h-2.5" /> {fmtRelTime(group.last_scrape_at)}</span>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-bold ${st.color} ${st.bg} border ${st.border}`}>
          <StIcon className={`w-3 h-3 ${st.spin ? "animate-spin" : ""}`} />
          {st.label}
        </span>
      </div>

      <div className="px-5 py-5 space-y-4">
        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5 text-[11px]">
            <span className="text-gray-500 font-semibold">Scrape progress</span>
            <span className="font-mono tabular-nums text-gray-700">
              <span className="font-bold">{ok}</span>
              <span className="text-gray-400">/{total}</span>
              <span className="text-gray-400 ml-1">({pct}%)</span>
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-700 ${
                group.last_scrape_status === "failed"
                  ? "bg-gradient-to-r from-rose-400 to-rose-600"
                  : group.last_scrape_status === "running"
                    ? "bg-gradient-to-r from-amber-400 to-amber-500 animate-pulse"
                    : "bg-gradient-to-r from-violet-500 to-indigo-600"
              }`}
              style={{ width: `${Math.max(pct, group.last_scrape_status === "running" ? 4 : 0)}%` }}
            />
          </div>
        </div>

        {/* Status hint */}
        {group.last_scrape_status === "running" && (
          <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin mt-0.5 shrink-0" />
            <div>
              Apify is fetching <span className="font-semibold">{group.asins.length} ASIN{group.asins.length !== 1 ? "s" : ""}</span> from amazon.{(group.marketplace || "in").toLowerCase()}…
              This usually takes <span className="font-semibold">15-30 seconds</span>. The card will appear in Market Research Analytics automatically when done.
            </div>
          </div>
        )}
        {group.last_scrape_status === "completed" && ok > 0 && (
          <div className="px-3 py-2.5 rounded-lg bg-violet-50 border border-violet-200 text-[11px] text-violet-800 flex items-start gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              Scrape complete — open <span className="font-semibold">Market Research → Analytics</span> and click the <span className="font-semibold">violet card</span> labelled "{group.name}" to see the full Deep Dive.
            </div>
          </div>
        )}
        {group.last_scrape_status === "completed" && ok === 0 && (
          <div className="px-3 py-2.5 rounded-lg bg-rose-50 border border-rose-200 text-[11px] text-rose-800 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              All ASINs failed to scrape. Common causes: invalid ASIN, product not available in the selected marketplace, or Apify rate-limiting. Try Re-scrape.
            </div>
          </div>
        )}
        {group.last_scrape_status === "failed" && (
          <div className="px-3 py-2.5 rounded-lg bg-rose-50 border border-rose-200 text-[11px] text-rose-800 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>The scrape job crashed. Check the backend logs or click Re-scrape.</div>
          </div>
        )}

        {/* ASIN chips */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">ASINs in this group</div>
          <div className="flex flex-wrap gap-1">
            {group.asins.slice(0, 50).map(a => (
              <span key={a} className="px-1.5 py-0.5 text-[10.5px] font-mono bg-gray-50 border border-gray-200 rounded text-gray-700 tabular-nums">{a}</span>
            ))}
            {group.asins.length > 50 && (
              <span className="px-1.5 py-0.5 text-[10.5px] text-gray-400">+{group.asins.length - 50} more</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => onDelete(group)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-gray-500 hover:text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition-all"
            data-testid="research-groups-latest-delete"
          >
            <Trash2 className="w-3 h-3" />
            Delete group
          </button>
          <button
            onClick={() => onRescrape(group)}
            disabled={group.last_scrape_status === "running"}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-violet-700 hover:bg-violet-50 border border-violet-200 hover:border-violet-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="research-groups-latest-rescrape"
          >
            <RefreshCw className={`w-3 h-3 ${group.last_scrape_status === "running" ? "animate-spin" : ""}`} />
            Re-scrape
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResearchGroupsModule;
